<template>
  <Teleport to="body">
    <Transition name="dcm-glass-modal">
      <div
        v-if="show"
        class="dcm-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dcm-modal-title"
        @click.self="closeModal"
      >
        <div class="dcm-modal" :class="modalClass" @click.stop>
          <header class="dcm-modal-header">
            <h3 id="dcm-modal-title" class="dcm-modal-title">
              {{ title || 'Confirmation' }}
            </h3>
            <SecondaryButton
              label=""
              icon="bi bi-x-lg"
              custom-class="dcm-modal-close-btn"
              aria-label="Close"
              :disabled="loading"
              @click="closeModal"
            />
          </header>

          <div class="dcm-icon-wrap">
            <div class="dcm-icon" aria-hidden="true">
              <i class="bi" :class="iconClass"></i>
            </div>
          </div>

          <div class="dcm-modal-body">
            <p class="dcm-message">{{ message || 'Are you sure you want to delete this item?' }}</p>

            <div v-if="details && Object.keys(details).length > 0" class="dcm-details">
              <span class="dcm-details-title">Details</span>
              <ul>
                <li v-for="(value, key) in details" :key="key">
                  <strong>{{ formatLabel(key) }}:</strong> {{ value }}
                </li>
              </ul>
            </div>

            <p v-if="showWarning" class="dcm-warning">
              <i class="bi bi-shield-exclamation" aria-hidden="true"></i>
              This action cannot be undone.
            </p>
          </div>

          <footer class="dcm-modal-footer">
            <SecondaryButton
              :label="cancelText || 'Cancel'"
              icon="bi bi-x-lg"
              :disabled="loading"
              @click="closeModal"
            />
            <PrimaryButton
              :label="loading ? resolvedConfirmLoadingText : (confirmText || defaultConfirmLabel)"
              :icon="resolvedConfirmIcon"
              custom-class="dcm-confirm-btn"
              :loading="loading"
              :disabled="loading"
              @click="confirmDelete"
            />
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import './DeleteConfirmationModal.css';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'DeleteConfirmationModal',
  components: {
    PrimaryButton,
    SecondaryButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Confirm Delete',
    },
    message: {
      type: String,
      default: 'Are you sure you want to delete this item?',
    },
    details: {
      type: Object,
      default: () => ({}),
    },
    confirmText: {
      type: String,
      default: 'Yes, Delete',
    },
    cancelText: {
      type: String,
      default: 'Cancel',
    },
    showWarning: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    /** `danger` = delete (red), `confirm` = neutral action (indigo) */
    variant: {
      type: String,
      default: 'danger',
      validator: (v) => ['danger', 'confirm'].includes(v),
    },
    confirmIcon: {
      type: String,
      default: '',
    },
    confirmLoadingText: {
      type: String,
      default: '',
    },
  },
  emits: ['confirm', 'cancel'],
  computed: {
    modalClass() {
      return this.variant === 'confirm' ? 'dcm-modal--confirm' : 'dcm-modal--danger';
    },
    iconClass() {
      if (this.variant === 'confirm') {
        return 'bi-check-circle-fill';
      }
      return 'bi-exclamation-triangle-fill';
    },
    defaultConfirmLabel() {
      return this.variant === 'confirm' ? 'Yes' : 'Yes, Delete';
    },
    resolvedConfirmIcon() {
      if (this.confirmIcon) return this.confirmIcon;
      return this.variant === 'confirm' ? 'bi bi-check-lg' : 'bi bi-trash';
    },
    resolvedConfirmLoadingText() {
      if (this.confirmLoadingText) return this.confirmLoadingText;
      return this.variant === 'confirm' ? 'Please wait…' : 'Deleting...';
    },
  },
  methods: {
    confirmDelete() {
      this.$emit('confirm');
    },
    closeModal() {
      if (!this.loading) {
        this.$emit('cancel');
      }
    },
    formatLabel(key) {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
    },
  },
};
</script>
